import * as React from 'react';
import * as ReactRouter from 'react-router';
import { connect } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { accessibilityUtils, fonts, media, mixins } from '@blend/consumer-components';

import * as footerConfigs from 'login-components/Common/Footer/footerConfigs';

import AppFooter from 'login-components/AppFooter/AppFooter';
import AppHeader from 'login-components/AppHeader/AppHeader';
import BlendLogo from 'login-components/BlendLogo/BlendLogo';
import Masthead from 'login-components/Masthead/Masthead';
import TextScreenModalTrigger from 'login-components/Common/TextScreenModal/TextScreenModalTrigger';

import OutdatedBrowserWarning from 'login-components/OutdatedBrowserWarning/OutdatedBrowserWarning';
import States, { isState } from 'login-constants/states';
import * as deploymentColors from 'login-stylesheets/deploymentColors';

import * as userUtils from 'login-utils/userUtils';

const footerHeight = 80;

const styles = StyleSheet.create({
  wrapper: {
    ...mixins.padding(0, 15, footerHeight),
    minHeight: '100vh',
    position: 'relative',
    overflowY: 'auto',
  },
  footerGroup: {
    ...mixins.padding(0, 30, 20),
    height: footerHeight,
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,

    display: 'flex',
    flexDirection: 'column',
    [media.smallView]: {
      textAlign: 'center',
      alignItems: 'center',
    },

    [media.mediumView]: {
      textAlign: 'right',
      alignItems: 'flex-end',
    },
  },
  logoWrapper: {
    ':not(:hover)': {
      opacity: 0.7,
    },
  },
  hashTermsLink: {
    ...fonts.Whitney,
    marginTop: 2,
    fontSize: 11,
    color: deploymentColors.loginBackgroundDynamicColor,
    textDecoration: 'underline',
  },
  termsLink: {
    ...fonts.Whitney,
    marginTop: 2,
    fontSize: 11,
    color: deploymentColors.loginBackgroundDynamicColor,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
});

interface AppProps extends ReactRouter.RouteComponentProps<{}, {}> {
  title: string | null;
  footer: FooterConfig | null;
  isOutdatedBrowser: boolean;
}

export class App extends React.Component<AppProps> {
  displayFastForward(location) {
    return !(isState(location.pathname, States.FAST_FORWARD) || userUtils.getCurrentUser());
  }

  render() {
    // location must exist in props, although it's typed optional in react router.
    const { isOutdatedBrowser, footer, children, title, location } = this.props;

    if (isOutdatedBrowser) {
      return <OutdatedBrowserWarning />;
    }

    return (
      <div className={css(styles.wrapper)}>
        <div>
          <Masthead />
          <AppHeader text={title!} />
          <section>
            {children}
            {footer && <AppFooter id="it-Footer" config={footer} />}
            {window.ALLOW_FAST_FORWARD && this.displayFastForward(location) && (
              <AppFooter id="it-Footer-FastFoward" config={footerConfigs.linkToFastForward} />
            )}
          </section>
        </div>
        <div className={css(styles.footerGroup)}>
          <span className={css(styles.logoWrapper)}>
            <BlendLogo format="responsive" />
          </span>
          <TextScreenModalTrigger
            textScreenId={'terms'}
            headerText="Terms Of Use"
            renderTrigger={({ openModal }) => (
              <span
                className={css(styles.termsLink)}
                onClick={openModal}
                onKeyDown={openModal && accessibilityUtils.generateOnKeyDown(openModal)}
                tabIndex={0}
              >
                Blend Terms of Use
              </span>
            )}
          />
        </div>
      </div>
    );
  }
}

export default connect((state: ReduxState) => ({
  title: state.layout.title,
  footer: state.layout.footer,
  isOutdatedBrowser: state.global.isOutdatedBrowser,
}))(App);
